import React, { useEffect, useRef, useState } from "react";
import {
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  useMediaQuery,
  Box,
} from "@mui/material";
import { assignRefs } from "../../Helpers/Actions";

const headerCellStyle = {
  fontSize: "14px",
  color: "rgba(62, 63, 76, 0.75)",
  fontWeight: 400,
  padding: "13px 16px",
  borderBottom: "1px solid rgba(234, 234, 234, 1)",
  borderTop: "1px solid rgba(234, 234, 234, 1)",
  whiteSpace: "nowrap",
  overflow: "hidden",
  textOverflow: "ellipsis",
};

const tableCellStyles = {
  color: "rgba(62, 63, 76, 1)",
  fontWeight: 400,
  borderBottom: "1px solid rgba(234, 234, 234, 1)",
  padding: "16px",
  whiteSpace: "nowrap",
  overflow: "hidden",
  textOverflow: "ellipsis",
};

const CustomTable = (props) => {
  const {
    columns,
    data,
    containerRef,
    footerSlot,
    stickyHeader = true,
    containerStyle = {},
    tableStyle = {},
    headerCellStyleProps = {},
    bodyCellStyleProps = {},
  } = props;
  const [hasHorizontalScroll, setHasHorizontalScroll] = useState(false);
  const isTablet = useMediaQuery("(max-width:1279px)");

  const internalRef = useRef(null);
  // Check for horizontal scroll
  useEffect(() => {
    const checkScroll = () => {
      const container = internalRef.current;
      if (!container) return;
      setHasHorizontalScroll(container.scrollWidth > container.clientWidth);
    };

    checkScroll();
    window.addEventListener("resize", checkScroll);
    return () => {
      window.removeEventListener("resize", checkScroll);
    };
  }, [columns, data]);

  return (
    <Box
      className="custom-table-container"
      sx={{
        position: "relative",
        display: "flex",
        flexDirection: "column",
        width: "100%",
        height: "100%",
        overflow: "hidden",
      }}
    >
      <TableContainer
        ref={assignRefs(internalRef, containerRef)}
        className="custom-scrollbar"
        sx={{
          height: "100%",
          overflow: "auto",
          position: "relative",
          flex: 1,
          paddingRight: "8px",
          paddingBottom: "16px",
          borderTopLeftRadius: "16px",
          borderTopRightRadius: "16px",
          width: isTablet ? "calc(100vw - 16px)" : "100%",
          maxWidth: isTablet ? "calc(100vw - 16px)" : "none",
          ...containerStyle,
        }}
      >
        <Table
          stickyHeader={stickyHeader}
          sx={{
            backgroundColor: "rgba(255, 255, 255, 1)",
            borderRadius: "16px",
            borderTop: "none",
            ...tableStyle,
          }}
        >
          <TableHead sx={{ position: "relative", zIndex: 99 }}>
            <TableRow>
              {columns.map((column, index) => (
                <TableCell
                  key={`header-${column.id || index}`}
                  sx={{
                    position: "sticky",
                    left: 0,
                    backgroundColor: "white",
                    zIndex: index === 0 ? 9 : 1,
                    boxShadow:
                      index === 0 && hasHorizontalScroll
                        ? "4px 0px 8px -3px rgba(0, 0, 0, 0.12)"
                        : "none",
                    fontWeight: 500,
                    borderTopLeftRadius: index === 0 ? "16px" : "0px",
                    borderTopRightRadius:
                      index === columns.length - 1 ? "16px" : "0px",
                    ...headerCellStyle,
                    ...headerCellStyleProps,
                    ...column.headerStyle,
                  }}
                  onClick={column.onHeaderClick}
                  align={column.align || "left"}
                >
                  {column.headerRender ? column.headerRender() : column.label}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>

          <TableBody>
            {data.map((row, rowIndex) => {
              const isLastRow = rowIndex === data.length - 1;
              return (
                <TableRow key={`row-${rowIndex}`}>
                  {columns.map((column, colIndex) => {
                    const isFirstCol = colIndex === 0;
                    const isLastCol = colIndex === columns.length - 1;

                    return (
                      <TableCell
                        key={`cell-${rowIndex}-${column.id || colIndex}`}
                        sx={{
                          position: isFirstCol ? "sticky" : "static",
                          left: 0,
                          backgroundColor: "white",
                          zIndex: 1,
                          boxShadow:
                            isFirstCol && hasHorizontalScroll
                              ? "4px 0px 8px -3px rgba(0, 0, 0, 0.12)"
                              : "none",
                          borderBottomLeftRadius:
                            isLastRow && isFirstCol ? "16px" : undefined,
                          borderBottomRightRadius:
                            isLastRow && isLastCol ? "16px" : undefined,
                          ...tableCellStyles,
                          ...bodyCellStyleProps,
                          ...column.cellStyle,
                          borderBottom: isLastRow
                            ? "none"
                            : "1px solid rgba(234, 234, 234, 1)",
                        }}
                        align={column.align || "left"}
                      >
                        {column.render ? column.render(row) : row[column.field]}
                      </TableCell>
                    );
                  })}
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
        {/* Rendered INSIDE the scrolling container on purpose: an infinite-scroll
            sentinel only works if it is a descendant of the element that
            actually scrolls, which for this table is TableContainer, not the
            page. */}
        {footerSlot}
      </TableContainer>
    </Box>
  );
};

export default CustomTable;
